import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useVapidConfig } from '@/hooks/useVapidConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, BellRing, AlertCircle, Smartphone, Loader2, Send, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type PushState = 'loading' | 'unsupported' | 'denied' | 'not-subscribed' | 'subscribed' | 'config-error';

// Helper to convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

export function PushNotificationManager() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const vapidConfig = useVapidConfig();
  const [pushState, setPushState] = useState<PushState>('loading');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!vapidConfig.loading) {
      checkPushSupport();
    }
  }, [user?.id, vapidConfig.loading]);

  const checkPushSupport = async () => {
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      try {
        const { receive } = await FirebaseMessaging.checkPermissions();
        if (receive === 'denied') {
          setPushState('denied');
          return;
        }

        // Se permitido, mas precisamos checar a inscrição atual
        if (user?.id && receive === 'granted') {
          const { token } = await FirebaseMessaging.getToken().catch(() => ({ token: null }));
          if (token) {
            const { data, error } = await supabase
              .from('push_subscriptions')
              .select('id')
              .eq('subscription->>token', token)
              .eq('user_id', user.id)
              .limit(1);

            if (!error && data && data.length > 0) {
              setPushState('subscribed');
              return;
            }
          }
        }
        setPushState('not-subscribed');
      } catch (error) {
        console.error('Error checking native push status:', error);
        setPushState('not-subscribed');
      }
      return;
    }

    // Web Push Logic (unchanged)
    if (!vapidConfig.configured || !vapidConfig.valid) {
      setPushState('config-error');
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushState('unsupported');
      return;
    }

    const permission = Notification.permission;
    if (permission === 'denied') {
      setPushState('denied');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription && user?.id) {
        const subscriptionJson = subscription.toJSON();
        const endpoint = subscriptionJson.endpoint;

        const { data } = await supabase
          .from('push_subscriptions')
          .select('id, user_id')
          .eq('subscription->>endpoint', endpoint)
          .limit(1);

        if (data && data.length > 0) {
          if (data[0].user_id === user.id) {
            setPushState('subscribed');
          } else {
            setPushState('not-subscribed');
          }
        } else {
          setPushState('not-subscribed');
        }
      } else {
        setPushState('not-subscribed');
      }
    } catch (error) {
      console.error('Error checking push subscription:', error);
      setPushState('not-subscribed');
    }
  };

  const sendTestNotification = async () => {
    if (!user?.id) {
      toast.error('Você precisa estar logado');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await vapidConfig.sendTestPush(user.id);

      if (result.success) {
        setTestResult({
          success: true,
          message: `Push enviado com sucesso! (${result.sent}/${result.total} dispositivos)`
        });
        toast.success('Notificação de teste enviada!');
      } else {
        setTestResult({
          success: false,
          message: result.error || 'Falha ao enviar notificação de teste'
        });
        toast.error(result.error || 'Erro ao enviar teste');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setTestResult({ success: false, message: errorMessage });
      toast.error('Erro ao enviar teste');
    } finally {
      setIsTesting(false);
    }
  };

  const subscribeToPush = async () => {
    if (!user?.id) {
      toast.error('Você precisa estar logado');
      return;
    }

    setIsProcessing(true);
    setTestResult(null);

    try {
      if (Capacitor.isNativePlatform()) {
        const { receive } = await FirebaseMessaging.requestPermissions();
        
        if (receive !== 'granted') {
          setPushState('denied');
          toast.error('Permissão negada para notificações');
          return;
        }

        const { token } = await FirebaseMessaging.getToken();
        
        if (token) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('subscription->>token', token);

          const { error } = await supabase
            .from('push_subscriptions')
            .insert([{
              user_id: user.id,
              subscription: {
                type: 'fcm',
                token: token,
                platform: Capacitor.getPlatform()
              },
              user_agent: navigator.userAgent
            }]);

          if (error) throw error;
          setPushState('subscribed');
          toast.success('Notificações ativadas!');
        }
      } else {
        const vapidPublicKey = vapidConfig.publicKey;
        if (!vapidPublicKey) {
          console.error('VAPID public key not configured');
          toast.error('Notificações push não configuradas. Verifique as chaves VAPID.');
          return;
        }

        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
          setPushState('denied');
          toast.error('Permissão negada para notificações');
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        const subscriptionJson = subscription.toJSON();
        const endpoint = subscriptionJson.endpoint;

        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('subscription->>endpoint', endpoint);

        const { error } = await supabase
          .from('push_subscriptions')
          .insert([{
            user_id: user.id,
            subscription: subscriptionJson as any,
            user_agent: navigator.userAgent
          }]);

        if (error) throw error;
        setPushState('subscribed');
        toast.success('Notificações push ativadas!');
      }
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Erro ao ativar notificações');
    } finally {
      setIsProcessing(false);
    }
  };

  const unsubscribeFromPush = async () => {
    if (!user?.id) return;

    setIsProcessing(true);

    try {
      if (Capacitor.isNativePlatform()) {
        const { token } = await FirebaseMessaging.getToken().catch(() => ({ token: null }));
        if (token) {
          await FirebaseMessaging.deleteToken();
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('subscription->>token', token);
        }
      } else {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          const subscriptionJson = subscription.toJSON();
          const endpoint = subscriptionJson.endpoint;

          await subscription.unsubscribe();

          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('subscription->>endpoint', endpoint);
        }
      }

      setPushState('not-subscribed');
      toast.success('Notificações push desativadas');
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Erro ao desativar notificações');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderContent = () => {
    switch (pushState) {
      case 'loading':
        return (
          <div className="flex items-center justify-center py-4">
            <CareOSLoader size="md" />
          </div>
        );

      case 'config-error':
        return (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Erro na configuração de push</p>
              <p className="text-sm mt-1">
                {vapidConfig.validationError || 'As chaves VAPID não estão configuradas corretamente.'}
              </p>
              <p className="text-xs mt-2 text-muted-foreground">
                Verifique se VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY e VAPID_SUBJECT estão configurados.
              </p>
            </AlertDescription>
          </Alert>
        );

      case 'unsupported':
        return (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {Capacitor.isNativePlatform()
                ? "Este dispositivo não parece suportar as configurações de push necessárias ou as permissões foram negadas permanentemente."
                : "Seu navegador não suporta notificações push. Para receber notificações no iOS, adicione o app à tela inicial e use o Safari 16.4 ou superior."
              }
            </AlertDescription>
          </Alert>
        );

      case 'denied':
        return (
          <Alert>
            <BellOff className="h-4 w-4" />
            <AlertDescription>
              Notificações foram bloqueadas. Para ativá-las, vá nas configurações do seu navegador
              e permita notificações para este site.
            </AlertDescription>
          </Alert>
        );

      case 'subscribed':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <BellRing className="w-5 h-5 text-success" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Notificações ativas</p>
                <p className="text-sm text-muted-foreground">
                  Você receberá alertas mesmo com o app fechado
                </p>
              </div>
              <Badge variant="default" className="bg-success">Ativo</Badge>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={unsubscribeFromPush}
                disabled={isProcessing || isTesting}
                className="w-full"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BellOff className="w-4 h-4 mr-2" />
                )}
                Desativar notificações neste dispositivo
              </Button>
            </div>
          </div>
        );

      case 'not-subscribed':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Bell className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Notificações desativadas</p>
                <p className="text-sm text-muted-foreground">
                  {Capacitor.isNativePlatform()
                    ? "Permissões de notificação podem estar desativadas"
                    : "Ative para receber alertas importantes em tempo real"
                  }
                </p>
              </div>
              <Badge variant="secondary">Inativo</Badge>
            </div>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BellRing className="w-4 h-4 mr-2" />
              )}
              Ativar notificações push
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              <Smartphone className="w-3 h-3 inline mr-1" />
              {Capacitor.isNativePlatform()
                ? "Requer permissões do sistema ativadas."
                : "Funciona em Android e iOS (Safari 16.4+)"
              }
            </p>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                    Ativar notificações neste dispositivo?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      As notificações push serão ativadas <strong>neste dispositivo</strong>.
                    </p>
                    <p className="text-sm">
                      Suas notificações em outros dispositivos continuarão funcionando normalmente.
                    </p>
                    <p className="text-sm">
                      Você pode desativar a qualquer momento nesta tela.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                    setShowConfirmDialog(false);
                    subscribeToPush();
                  }}>
                    Ativar neste dispositivo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {t('settings.pushNotifications')}
        </CardTitle>
        <CardDescription>
          {t('settings.pushNotificationsDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
}
